import EmptyInvoicesComponent from 'widgets/Invoices/EmptyInvoicesComponent.jsx';
import { useInvoiceMutations } from 'entities/invoice/model';
import {
  ContentCotainer,
  ContentLoadingState,
} from 'shared/ui/layouts/PageContent';
import { Table } from 'shared/ui/Table/TableBlocks';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDialog } from 'shared/components/Dialog';
import { formatDataForCSV } from 'entities/invoice/lib';
import { InvoiceDetailsSlideOver } from './ui/InvoiceDetailsSlideOver';
import { CSVLink } from 'react-csv';
import {
  XCircleIcon,
  DocumentIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import { tokenToImageV1 } from 'shared/lib';
import { Badge } from 'flowbite-react';
import classnames from 'classnames';
import { formatDollarAmount } from 'shared/lib/string';
import { useCurrentUserDetails } from 'entities/user/model';
import { ROLES } from 'shared/hooks';
import { getTokenHumanReadableNameByChain } from '../bill/lib';
import { PageHeader } from '../../shared/ui/layouts/PageHeader';
import { fetchInvoice } from '../../entities/invoice/model/actions';
import { UpdateInvoicePaymentStatusModal } from './UpdatePaymentStatusModal';
import { useOrganizationInvoices } from './useOrganizationInvoices';
import { customFlowBiteTheme, TabItem, Tabs } from '../../shared/ui/Tabs/Tabs';
import { RecurringInvoicesPage } from './recurring/RecurringInvoicesPage';
import { BellAlertIcon } from '@heroicons/react/20/solid';
import { useBatchInvoicePaymentReminder } from '../../entities/invoice/useRemindInvoicePayment';
import { toast } from 'react-toastify';
import { AddFilterModal } from './AddFilterModal';
import { useAuth0 } from '@auth0/auth0-react';

export const InvoicesPage = () => {
  const { getAccessTokenSilently } = useAuth0();
  const {
    invoices,
    invoicesTotal,
    isLoading,
    isFetching,
    hasFilterOptions,
    updateQueryOptions,
    queryOptions,
  } = useOrganizationInvoices();
  const dialog = useDialog();
  const navigate = useNavigate();
  const [showAddFilterModal, setShowAddFilterModal] = useState(false);
  const [viewInvoiceDetails, setViewInvoiceDetails] = useState(null);
  const { remove } = useInvoiceMutations();
  const { isLoading: isReminding, remindInvoicePayment } =
    useBatchInvoicePaymentReminder();
  const { getCurrentOrgRole } = useCurrentUserDetails();
  const [invoiceToUpdatePaymentStatus, setInvoiceToUpdatePaymentStatus] =
    useState(null);
  const [rowSelection, setRowSelection] = useState({});
  const [deleteLoading, setDeleteLoading] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const csvExportData = formatDataForCSV(
    Object.keys(rowSelection).map((idx) => invoices[idx])
  );

  const _formSecondaryActionList = () => {
    const res = [];

    if (!!Object.keys(rowSelection).length) {
      res.push({
        label: (
          <CSVLink data={csvExportData} filename={today + '-invoices.csv'}>
            Export to CSV
          </CSVLink>
        ),
        icon: () => <DocumentIcon className="w-6 mr-2" />,
      });

      res.push({
        label: 'Send Payment Reminder',
        icon: () => <BellAlertIcon className="w-5 mr-2" />,
        onClick: () =>
          remindInvoicePayment({
            invoiceIds: Object.keys(rowSelection)
              .map((idx) => invoices[idx]?._id)
              .filter((id) => !!id),
          })
            .then(() => {
              toast.success(
                'Successfully send payments reminders to all recipients!'
              );
            })
            .catch(() => {
              toast.error(
                'Failed to send payments reminders. Contact team@getfractal.xyz and we will fix it!'
              );
            }),
      });

      res.push({
        label: <span className="text-rose-500">Delete Selected Invoices</span>,
        icon: () => <XCircleIcon className="text-rose-500 w-6 mr-2" />,
        onClick: () => handleBatchDelete(),
      });
    }

    return res;
  };

  const _handleTableFilterChange = () => {
    setShowAddFilterModal(true);
  };

  const _handleFilterPopupClose = () => {
    setShowAddFilterModal(false);
  };

  const handleFilterPopupSearchChange = (searchState) => {
    updateQueryOptions(searchState);
  };

  const _handlePaginationChange = (state) => {
    updateQueryOptions({
      pageNumber: state.pageIndex + 1,
      pageLimit: state.pageSize,
    });
  };

  const _handleTableSearchChange = (change) => {
    updateQueryOptions({
      generalSearchString: change.searchString,
    });
  };

  const handleBatchDelete = useCallback(async () => {
    const confirmationText =
      'Are you sure you want to delete this invoices(s)? This action cannot be undone and you and your team members will not able to see this invoice anymore.';

    dialog.confirm({
      title: 'Delete Invoice(s)',
      desc: confirmationText,
      onConfirm: async () => {
        try {
          setDeleteLoading(true);
          await Promise.all(
            Object.keys(rowSelection).map((idx) =>
              remove.mutate(invoices[idx]._id)
            )
          );
        } catch (error) {
          console.error(error);
        } finally {
          setRowSelection({});
          setDeleteLoading(false);
        }
      },
    });
  }, [dialog, rowSelection, remove, invoices]);

  const _getRowActions = (row) => {
    const actions = [];

    actions.push(
      {
        label: 'Update Payment Status',
        onClick: () => {
          setInvoiceToUpdatePaymentStatus(row);
        },
      },
      {
        label: 'Copy Payment Link',
        onClick: () => {
          getAccessTokenSilently()
            .then((t) => fetchInvoice(row._id, t))
            .then((d) => {
              navigator.clipboard.writeText(
                window.location.origin + `/pay?id=${row._id}&pac=${d.pac}`
              );
              toast.success('Copied to clipboard');
            });
        },
      }
    );

    return actions;
  };

  const columns = useMemo(() => {
    const res = [
      {
        id: 'selection',
      },
      {
        header: 'Client Name',
        cell: ({ row: { original: invoice } }) => (
          <div className="flex flex-col text-left">
            <span className="text-sm text-white font-medium">
              {invoice.client_name}
            </span>
            <span className="mt-0.5 flex items-center text-xs font-normal text-slate-400">
              {invoice.recurrenceRuleId && (
                <ArrowPathIcon className="mr-1 w-4 " />
              )}
              {invoice.client_email}
            </span>
          </div>
        ),
      },
      {
        header: 'Amount',
        cell: ({ row: { original: invoice } }) => (
          <>
            <div className="flex items-center">
              <span className="mr-1">{tokenToImageV1(invoice.token)}</span>
              <span className="mr-2 dark:text-white">
                {formatDollarAmount(invoice.amount)}
              </span>
            </div>

            <div className="leading-none">
              <span className=" flex items-center text-[10px] xl:text-xs">
                {getTokenHumanReadableNameByChain(
                  invoice.token,
                  invoice.network
                )}{' '}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-3">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m9 20.247 6-16.5"
                  />
                </svg>
                {invoice.network}
              </span>
            </div>
          </>
        ),
      },
      {
        header: 'Due Date',
        cell: ({ row: { original: invoice } }) => (
          <span>{dayjs(invoice.payments_date).format('DD MMM YY')}</span>
        ),
      },
      {
        header: 'Accounting Status',
        cell: ({ row: { original: invoice } }) => (
          <>
            <ReconciliationCell invoice={invoice} />
          </>
        ),
      },
      {
        header: 'Status',
        cell: ({ row: { original: inv } }) => <StatusCell invoice={inv} />,
      },
      {
        header: 'actions',
      },
    ];

    if (getCurrentOrgRole() === ROLES.ACCOUNTANT) {
      res.splice(5, 0, {
        header: 'Invoice ID',
        cell: ({ row: { original: invoice } }) => (
          <InvoiceReference invoice={invoice} />
        ),
      });
    }

    return res;
  }, []);

  if (isLoading) {
    return <ContentLoadingState />;
  }

  return (
    <ContentCotainer className="invoice-page-container">
      {!hasFilterOptions() && invoices.length === 0 && !isFetching ? (
        <EmptyInvoicesComponent />
      ) : (
        <>
          <PageHeader
            title="Payments In"
            subtitle="Overview of your paid & unpaid invoices"
          />

          <Table
            primaryActionText="Add New Invoice"
            onSearchChange={_handleTableSearchChange}
            onPrimaryAction={() => {
              navigate('/dashboard/create-invoice');
            }}
            manualPagination
            filterState={{
              type: 'external',
              isFilterApplied: hasFilterOptions(),
            }}
            onFilterChange={_handleTableFilterChange}
            data={invoices}
            columns={columns}
            isDataLoading={isFetching || deleteLoading || isReminding}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            onRowClick={setViewInvoiceDetails}
            onPaginationChange={_handlePaginationChange}
            getRowActions={_getRowActions}
            initPagination={{
              pageIndex: queryOptions.pageNumber - 1,
              pageSize: queryOptions.pageLimit,
            }}
            totalCount={invoicesTotal}
            secondaryActionList={_formSecondaryActionList()}
          />
        </>
      )}

      <AddFilterModal
        show={showAddFilterModal}
        queryOptions={queryOptions}
        onSearchChange={handleFilterPopupSearchChange}
        onClose={_handleFilterPopupClose}
      />

      <UpdateInvoicePaymentStatusModal
        invoice={invoiceToUpdatePaymentStatus}
        onClose={() => {
          setInvoiceToUpdatePaymentStatus(null);
        }}
      />

      <InvoiceDetailsSlideOver
        show={!!viewInvoiceDetails}
        invoice={viewInvoiceDetails}
        onClose={() => setViewInvoiceDetails(null)}
      />
    </ContentCotainer>
  );
};

export const InvoicesDashboard = () => {
  return (
    <Tabs
      theme={{
        ...customFlowBiteTheme,
        base: `${customFlowBiteTheme.base} h-full`,
        tablist: {
          ...customFlowBiteTheme.tablist,
          styles: {
            ...customFlowBiteTheme.tablist.styles,
            default: `${customFlowBiteTheme.tablist.styles.default} mx-4 sm:mx-6 md:mx-8`,
          },
        },
        tabitemcontainer: {
          ...customFlowBiteTheme.tabitemcontainer,
          base: `${customFlowBiteTheme.tabitemcontainer.base} h-full`,
        },
        tabpanel: `${customFlowBiteTheme.tabpanel} h-full`,
      }}
      aria-label="Invoices tabs"
      variant="default">
      <TabItem active title="Invoices">
        <InvoicesPage />
      </TabItem>
      <TabItem active title="Recurring Invoices" icon={ArrowPathIcon}>
        <RecurringInvoicesPage />
      </TabItem>
    </Tabs>
  );
};

const StatusCell = ({ invoice }) => {
  const _getPaymentStatus = () => {
    if (invoice.payment.status === 'Paid') {
      return 'Paid';
    }

    if (invoice.payment.status === 'Unpaid') {
      return 'Unpaid';
    }

    if (invoice.payment.status === 'Sent') {
      return 'Sent';
    }
  };

  const _getBgColorClassName = () => {
    if (invoice.payment.status === 'Paid') {
      return '!bg-slate-700 !text-emerald-400';
    }

    if (invoice.payment.status === 'Sent') {
      return '!bg-slate-700 !text-indigo-300';
    }

    return '!bg-slate-700 !text-slate-200';
  };

  return (
    <Badge className={classnames('inline', _getBgColorClassName())}>
      {_getPaymentStatus()}
    </Badge>
  );
};

const ReconciliationCell = ({ invoice }) => {
  if (invoice?.accountingStatus?.reconciled) {
    return (
      <div className="flex flex-col">
        <span>Reconciled</span>
        {!!invoice?.accountingStatus?.accountName && (
          <span className="text-xs text-slate-500">
            {invoice?.accountingStatus?.accountName}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <span>Not Reconciled</span>
      {!!invoice?.accountingStatus?.accountName && (
        <span className="text-xs text-slate-500">
          {invoice?.accountingStatus?.accountName}
        </span>
      )}
    </div>
  );
};

const InvoiceReference = ({ invoice }) => {
  return (
    <span>
      {invoice?.referenceId ? invoice.referenceId : 'Reference ID not found'}
    </span>
  );
};
